// w34 Yulduzli Tun — celestial night-sky wedding preview card.
//
// The full template is a deep-navy starlit invitation (gold + Cinzel).
// The catalog card keeps that mood: a navy→ink frame dusted with tiny
// gold stars, an inner navy card, and a focal medallion holding a
// crescent moon ringed by stars. Names in the template font; eyebrow /
// footer use the standard small-caps treatment.
import { type LayoutCtx } from "./_shared";

// Deterministic star positions so the card renders identically every
// time (no Math.random — a layout is a pure function of its ctx).
const STARS = [
  [12, 18], [27, 9], [44, 22], [63, 13], [81, 26], [91, 11],
  [8, 47], [88, 52], [17, 71], [38, 84], [58, 78], [79, 88],
  [49, 38], [70, 63], [23, 58], [92, 74],
];

export function StarryNightPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const frame = "linear-gradient(155deg, #0A0F24 0%, #050A18 55%, #0A0F24 100%)";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: frame,
        padding: "5%",
        display: "flex",
      }}
    >
      {/* star dust on the frame */}
      {STARS.map(([x, y], i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${x}%`,
            top: `${y}%`,
            width: i % 3 === 0 ? 3 : 2,
            height: i % 3 === 0 ? 3 : 2,
            borderRadius: "50%",
            background: i % 2 ? accent : "#E5D08C",
            boxShadow: `0 0 4px ${accent}`,
            opacity: 0.85,
          }}
        />
      ))}
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "9% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "7%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* crescent-moon medallion */}
        <div style={{ width: "60%", aspectRatio: "1 / 1", position: "relative", marginBottom: "7%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `1px solid ${accent}66`,
              display: "grid",
              placeItems: "center",
            }}
          >
            <svg viewBox="0 0 100 100" width="58%" height="58%" aria-hidden>
              {/* crescent: a gold disc masked by an offset bg disc */}
              <defs>
                <mask id="sn-crescent">
                  <rect width="100" height="100" fill="black" />
                  <circle cx="50" cy="50" r="34" fill="white" />
                  <circle cx="63" cy="42" r="30" fill="black" />
                </mask>
              </defs>
              <circle cx="50" cy="50" r="34" fill={accent} mask="url(#sn-crescent)" />
              {/* a few orbiting stars */}
              <path d="M30 20 l1.6 3.4 3.7.4-2.7 2.6.7 3.7-3.3-1.8-3.3 1.8.7-3.7-2.7-2.6 3.7-.4z" fill="#E5D08C" />
              <path d="M78 70 l1.2 2.6 2.8.3-2 2 .5 2.8-2.5-1.3-2.5 1.3.5-2.8-2-2 2.8-.3z" fill={accent} />
              <circle cx="74" cy="30" r="1.6" fill="#E5D08C" />
              <circle cx="26" cy="68" r="1.6" fill={accent} />
            </svg>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 26, lineHeight: 1.12, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "5%",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
